// SecurityPage — four customer-benefit pillars. Implementation specifics
// remain available under NDA.

import {
    ArrowRight,
    BadgeCheck,
    KeyRound,
    ShieldCheck,
    UserCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { buildBreadcrumbListSchema } from "@/lib/schema";

const PILLARS = [
    {
        icon: <ShieldCheck size={22} />,
        title: "Your data, isolated by construction.",
        body:
            "Your scenarios, your threats, your results — yours alone. Every read, every write, every result is bound to the customer that owns it. Cross-customer access is impossible by construction. There is no shared data plane and no UI-only filter that can be bypassed by hand-rolling an API call.",
        bullets: [
            "Ownership enforced on every API call — not at the screen layer.",
            "Cross-customer requests return as if the resource does not exist — no enumeration vector.",
            "Suspended or expired customers see no data, ever — not even cached state.",
        ],
        nda: "Detail under NDA.",
    },
    {
        icon: <KeyRound size={22} />,
        title: "Invite-only access. No public surface.",
        body:
            "There is no public sign-up. Operator accounts are provisioned by invitation, with single-use links, time-bounded sessions, and modern credential hygiene. Failed-login throttling is on by default. Multi-factor authentication is on the roadmap and called out openly in onboarding.",
        bullets: [
            "Invite-only operator provisioning, scoped to a specific access request.",
            "Session lifetimes are bounded — every customer can rotate them at any time.",
            "Industry-standard password hygiene with a common-credential block list.",
        ],
        nda: "Detail under NDA.",
    },
    {
        icon: <BadgeCheck size={22} />,
        title: "Every action, recorded. Every operator, named.",
        body:
            "Every login, every run state transition, and every change to a customer record is recorded in a tamper-resistant log. The retention window is configurable and ships ready to ingest into the customer's existing audit pipeline. Administrative actions are stamped with the operator that performed them.",
        bullets: [
            "Authentication events: login success, login failure, password change, invite accepted.",
            "Run lifecycle events: queued → running → cancelling → succeeded / failed / cancelled / deleted.",
            "Structured records, ready for ingest into the customer's preferred audit pipeline.",
        ],
        nda: "Detail under NDA.",
    },
    {
        icon: <UserCheck size={22} />,
        title: "Eligibility, attested. Embargoes, enforced.",
        body:
            "UAVSimNet is dual-use counter-UAS analytic software. Eligibility is restricted to defence, government, and licensed-industry teams. Embargoed jurisdictions are blocked at the access-request layer. Customers retain ownership of every scenario, parameter set, and run output they generate.",
        bullets: [
            "Compliance, terms, and acceptable-use attestation captured at signup with timestamps.",
            "Embargoed jurisdictions are not eligible for access at any tier.",
            "Customers retain ownership of all data they generate inside their tenant.",
        ],
        nda: "Detail under NDA.",
    },
];

export default function SecurityPage() {
    return (
        <>
            <SeoHead
                path="/security"
                schemaJsonLd={[
                    buildBreadcrumbListSchema({ path: "/security", label: "Security" }),
                ]}
            />
        <div data-testid="security-page">
            <section className="section" style={{ paddingTop: 64 }}>
                <div className="section__inner">
                    <span className="section__eyebrow">// SECURITY POSTURE</span>
                    <h1 className="section__heading">
                        Defence-grade by construction. Not by promise.
                    </h1>
                    <p className="section__lede">
                        UAVSimNet is built for defence, government, and licensed
                        industry. The four pillars below describe the posture every
                        customer receives by default — not features, not roadmap, not
                        policy slides. Implementation specifics (secret management,
                        key rotation, audit retention, deployment hardening) are
                        documented in full and shared under NDA during procurement
                        evaluation.
                    </p>
                </div>
            </section>
            {PILLARS.map((p, idx) => (
                <section
                    key={p.title}
                    className={"section" + (idx % 2 === 1 ? " section--alt" : "")}
                    data-testid={`security-pillar-${idx}`}
                >
                    <div
                        className="section__inner"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 2fr",
                            gap: 32,
                        }}
                    >
                        <div className="scroll-reveal">
                            <div className="cap-card__icon">{p.icon}</div>
                            <h2 className="section__heading" style={{ fontSize: 24 }}>
                                {p.title}
                            </h2>
                        </div>
                        <div className="scroll-reveal" data-delay="100">
                            <p
                                className="section__lede"
                                style={{ marginBottom: 18 }}
                            >
                                {p.body}
                            </p>
                            <ul className="cap-card__list">
                                {p.bullets.map((b) => (
                                    <li key={b}>{b}</li>
                                ))}
                            </ul>
                            <p
                                style={{
                                    marginTop: 16,
                                    color: "var(--ux-text-3)",
                                    fontSize: 13,
                                    fontStyle: "italic",
                                }}
                                data-testid={`security-pillar-${idx}-nda`}
                            >
                                {p.nda}
                            </p>
                        </div>
                    </div>
                </section>
            ))}
            <section className="section">
                <div className="section__inner" style={{ textAlign: "center" }}>
                    <h2 className="section__heading">
                        Need the engineering detail?
                    </h2>
                    <p
                        className="section__lede"
                        style={{ margin: "0 auto 24px" }}
                    >
                        Implementation specifics, threat-model documentation, and
                        deployment-hardening guides are released to qualified
                        evaluators under NDA. Start the conversation through the
                        contact form.
                    </p>
                    <Link
                        to="/contact"
                        className="btn-primary"
                        data-testid="security-cta-contact"
                    >
                        Request the security pack <ArrowRight size={16} />
                    </Link>
                    <Link
                        to="/evidence"
                        className="btn-ghost"
                        data-testid="security-cta-evidence"
                    >
                        Evidence-grade credibility framework
                    </Link>
                    <Link
                        to="/features"
                        className="btn-ghost"
                        data-testid="security-cta-features"
                    >
                        Feature tour
                    </Link>
                </div>
            </section>
        </div>
        </>
    );
}
